import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm, initials } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Search, Plus, Pencil, Trash2, UserCheck, DownloadCloud, Briefcase, FileText, Users, Award, CheckCircle2, XCircle, Star, Eye } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { StaffForm } from "@/pages/Staff";
import { toast } from "sonner";

const STATUSES = ["New", "Shortlisted", "Interviewed", "Selected", "Rejected"];
const ALL_STATUSES = [...STATUSES, "Joined"];
const SOURCES = ["Walk-in", "Referral", "Online Portal", "Advertisement", "Social Media", "Agency", "Other"];
const STATUS_COLOR = {
  New: "bg-blue-100 text-blue-700 border-blue-200",
  Shortlisted: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Interviewed: "bg-violet-100 text-violet-700 border-violet-200",
  Selected: "bg-amber-100 text-amber-700 border-amber-200",
  Rejected: "bg-rose-100 text-rose-700 border-rose-200",
  Joined: "bg-teal-100 text-teal-700 border-teal-200",
};
const EMPTY = {
  name: "", email: "", phone: "", whatsapp: "", dob: "", gender: "", marital_status: "", address: "",
  post_applied_for: "", staff_type: "Teaching", expected_salary: "", joining_availability: "", notice_period: "", source: "Walk-in", apply_date: "",
  qualification: "", specialization: "", board_university: "", passing_year: "", cgpa: "",
  organization: "", last_designation: "", experience: "",
  resume: "", resume_name: "", photo: "",
  status: "New", remarks: "", interview_at: "", rating: 0,
};

const STAT_META = [
  { key: "total", label: "Total Inquiries", icon: FileText, tint: "bg-blue-100 text-blue-600", sub: "All applications" },
  { key: "Shortlisted", label: "Shortlisted", icon: Users, tint: "bg-emerald-100 text-emerald-600" },
  { key: "Interviewed", label: "Interviewed", icon: Award, tint: "bg-violet-100 text-violet-600" },
  { key: "Selected", label: "Selected", icon: CheckCircle2, tint: "bg-amber-100 text-amber-600" },
  { key: "Rejected", label: "Rejected", icon: XCircle, tint: "bg-rose-100 text-rose-600" },
];

export default function StaffInquiry() {
  const { user } = useAuth();
  const canManage = hasPerm(user, "manage_leads");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [fStatus, setFStatus] = useState("all");
  const [fType, setFType] = useState("all");
  const [fPost, setFPost] = useState("all");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [joinLead, setJoinLead] = useState(null);
  const [staffFormOpen, setStaffFormOpen] = useState(false);
  const [resumeView, setResumeView] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/staff-leads").then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => setForm({ ...EMPTY, apply_date: new Date().toISOString().slice(0, 10) });
  const openEdit = (l) => setForm({ ...EMPTY, ...l });
  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onFile = (key, nameKey, file, maxMB) => {
    if (!file) return;
    if (file.size > maxMB * 1024 * 1024) { toast.error(`File must be under ${maxMB}MB`); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((p) => ({ ...p, [key]: reader.result, ...(nameKey ? { [nameKey]: file.name } : {}) }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Candidate name is required"); return; }
    setSaving(true);
    try {
      const { id, ...payload } = form;
      if (id) await api.put(`/staff-leads/${id}`, payload);
      else await api.post("/staff-leads", payload);
      toast.success(id ? "Inquiry updated" : "Inquiry added");
      setForm(null); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const changeStatus = async (l, status) => {
    setRows((r) => r.map((x) => (x.id === l.id ? { ...x, status } : x)));
    try { await api.patch(`/staff-leads/${l.id}/status`, { status }); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); load(); }
  };

  const patchMeta = async (l, patch) => {
    setRows((r) => r.map((x) => (x.id === l.id ? { ...x, ...patch } : x)));
    try { await api.patch(`/staff-leads/${l.id}/meta`, patch); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); load(); }
  };

  const doDelete = async () => {
    try { await api.delete(`/staff-leads/${delTarget.id}`); toast.success("Inquiry deleted"); setDelTarget(null); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const startJoin = (l) => { setJoinLead(l); setStaffFormOpen(true); };
  const onStaffJoined = async () => {
    if (joinLead) {
      try { await api.patch(`/staff-leads/${joinLead.id}/status`, { status: "Joined" }); }
      catch (e) { toast.error("Staff created but inquiry status not updated"); }
    }
  };

  const exportCsv = () => {
    const cols = ["name", "phone", "email", "post_applied_for", "staff_type", "qualification", "experience", "expected_salary", "source", "apply_date", "status"];
    const head = ["Name", "Phone", "Email", "Post Applied", "Type", "Qualification", "Experience", "Expected Salary", "Source", "Applied On", "Status"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [head.join(",")].concat(filtered.map((l) => cols.map((c) => esc(l[c])).join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "staff_inquiries.csv"; a.click();
    URL.revokeObjectURL(a.href);
  };

  const q = search.toLowerCase();
  const posts = Array.from(new Set(rows.map((l) => l.post_applied_for).filter(Boolean)));
  const filtered = rows.filter((l) => {
    if (fStatus !== "all" && l.status !== fStatus) return false;
    if (fType !== "all" && l.staff_type !== fType) return false;
    if (fPost !== "all" && l.post_applied_for !== fPost) return false;
    if (q && ![l.name, l.phone, l.email, l.post_applied_for].some((v) => (v || "").toLowerCase().includes(q))) return false;
    return true;
  });
  const total = rows.length;
  const count = (s) => rows.filter((l) => l.status === s).length;
  const pct = (n) => (total ? Math.round((n / total) * 100) : 0);
  const staffInitial = joinLead ? {
    name: joinLead.name, phone: joinLead.phone, email: joinLead.email, dob: joinLead.dob,
    gender: joinLead.gender || "Male", marital_status: joinLead.marital_status || "Unmarried",
    address: joinLead.address, designation: joinLead.post_applied_for, staff_type: joinLead.staff_type || "Teaching",
    qualification: joinLead.qualification, salary: joinLead.expected_salary || "",
    last_organisation: joinLead.organization, last_position: joinLead.last_designation,
    experience: joinLead.experience, photo: joinLead.photo || "",
  } : null;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-indigo-100 text-indigo-600 flex items-center justify-center"><Briefcase size={24} /></div>
          <div>
            <h1 className="font-heading text-3xl font-extrabold text-slate-900">Staff Inquiry for Job</h1>
            <p className="text-slate-500 text-sm">Manage teacher and staff job applications</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="gap-1.5" onClick={exportCsv} data-testid="staff-inquiry-export"><DownloadCloud size={16} />Export</Button>
          {canManage && <Button className="gap-1.5" onClick={openAdd} data-testid="add-staff-inquiry-btn"><Plus size={16} />Add Inquiry</Button>}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="staff-inquiry-stats">
        {STAT_META.map((m) => {
          const val = m.key === "total" ? total : count(m.key);
          return (
            <div key={m.key} className="bg-white rounded-2xl border border-slate-200 p-4 flex items-start gap-3 shadow-sm">
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${m.tint}`}><m.icon size={20} /></div>
              <div className="min-w-0">
                <p className="text-xs text-slate-500">{m.label}</p>
                <p className="text-2xl font-extrabold text-slate-900 leading-tight">{val}</p>
                <p className="text-xs text-slate-400">{m.key === "total" ? "All time" : `${pct(val)}% of total`}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-wrap gap-2 items-center">
        <Select value={fPost} onValueChange={setFPost}><SelectTrigger className="h-9 w-40" data-testid="si-post-filter"><SelectValue placeholder="All Posts" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Posts</SelectItem>{posts.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
        <Select value={fType} onValueChange={setFType}><SelectTrigger className="h-9 w-40" data-testid="si-type-filter"><SelectValue placeholder="All Types" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Types</SelectItem><SelectItem value="Teaching">Teaching</SelectItem><SelectItem value="Non-Teaching">Non-Teaching</SelectItem></SelectContent></Select>
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9 h-9" placeholder="Search by name, phone, email…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="si-search" />
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 text-sm">
        {[["all", `All (${total})`], ...STATUSES.map((s) => [s, `${s} (${count(s)})`])].map(([k, lbl]) => (
          <button key={k} onClick={() => setFStatus(k)} className={`px-3 py-1.5 rounded-lg font-medium ${fStatus === k ? "bg-indigo-600 text-white" : "bg-white border border-slate-200 text-slate-500 hover:bg-slate-50"}`} data-testid={`si-tab-${k}`}>{lbl}</button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200">
        {loading ? <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-indigo-500" /></div> :
          filtered.length === 0 ? <div className="py-16 text-center text-slate-400"><Briefcase size={40} className="mx-auto mb-2 opacity-40" />No staff inquiries yet.</div> : (
            <div className="overflow-auto max-h-[calc(100vh-420px)]">
              <table className="w-full text-sm border border-slate-200 border-collapse min-w-[1000px]">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider sticky top-0 z-10">
                  <tr>{["#", "Candidate", "Post Applied", "Rating", "Qualification", "Experience", "Applied On", "Interview", "Status", "Action"].map((h) => <th key={h} className={`px-3 py-2.5 border border-slate-200 bg-slate-50 ${h === "Action" ? "text-center" : "text-left"}`}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {filtered.map((l, i) => (
                    <tr key={l.id} className="hover:bg-slate-50" data-testid={`staff-inquiry-row-${l.id}`}>
                      <td className="px-3 py-2.5 border border-slate-200 text-slate-400">{i + 1}</td>
                      <td className="px-3 py-2.5 border border-slate-200">
                        <div className="flex items-center gap-2">
                          <div className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-600 overflow-hidden flex items-center justify-center text-xs font-bold shrink-0">{l.photo ? <img src={l.photo} alt={l.name} className="w-full h-full object-cover" /> : initials(l.name)}</div>
                          <div><div className="font-medium text-slate-800 leading-tight">{l.name}</div><div className="text-[11px] text-slate-400">{l.phone || l.email || "—"}</div></div>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 border border-slate-200">
                        <div className="text-slate-700">{l.post_applied_for || "—"}</div>
                        {l.staff_type && <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${l.staff_type === "Teaching" ? "bg-indigo-50 text-indigo-600" : "bg-amber-50 text-amber-700"}`}>{l.staff_type}</span>}
                      </td>
                      <td className="px-3 py-2.5 border border-slate-200"><Stars value={l.rating || 0} onChange={canManage ? (v) => patchMeta(l, { rating: v }) : null} readOnly={!canManage} testid={`si-rating-${l.id}`} /></td>
                      <td className="px-3 py-2.5 border border-slate-200 text-slate-600">{l.qualification || "—"}</td>
                      <td className="px-3 py-2.5 border border-slate-200 text-slate-600">{l.experience || "—"}</td>
                      <td className="px-3 py-2.5 border border-slate-200 text-slate-500">{l.apply_date || "—"}</td>
                      <td className="px-3 py-2.5 border border-slate-200">
                        {canManage ? <Input type="datetime-local" value={l.interview_at || ""} onChange={(e) => patchMeta(l, { interview_at: e.target.value })} className="h-7 text-xs px-1.5 w-[168px]" data-testid={`si-interview-${l.id}`} />
                          : <span className="text-xs text-slate-500">{l.interview_at ? fmtDT(l.interview_at) : "—"}</span>}
                      </td>
                      <td className="px-3 py-2.5 border border-slate-200">
                        {canManage && l.status !== "Joined" ? (
                          <Select value={l.status} onValueChange={(v) => changeStatus(l, v)}>
                            <SelectTrigger className={`h-7 text-xs w-32 border ${STATUS_COLOR[l.status] || ""}`} data-testid={`si-status-${l.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select>
                        ) : <span className={`px-2 py-0.5 rounded-full text-xs border ${STATUS_COLOR[l.status] || ""}`}>{l.status}</span>}
                      </td>
                      <td className="px-3 py-2.5 border border-slate-200">
                        <div className="flex items-center justify-center gap-1.5">
                          {l.resume && <Button size="sm" variant="ghost" className="h-8 px-2 text-indigo-600" title="Preview Resume" onClick={() => setResumeView(l)} data-testid={`si-resume-${l.id}`}><Eye size={15} /></Button>}
                          {canManage && <>
                            {l.status !== "Joined" && <Button size="sm" variant="outline" className="h-8 gap-1 text-teal-700 border-teal-200 hover:bg-teal-50" title="Join as Staff" onClick={() => startJoin(l)} data-testid={`si-join-${l.id}`}><UserCheck size={14} />Join</Button>}
                            <Button size="sm" variant="ghost" className="h-8 px-2" title="Edit" onClick={() => openEdit(l)} data-testid={`si-edit-${l.id}`}><Pencil size={15} /></Button>
                            <Button size="sm" variant="ghost" className="h-8 px-2 text-rose-600" title="Delete" onClick={() => setDelTarget(l)} data-testid={`si-delete-${l.id}`}><Trash2 size={15} /></Button>
                          </>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="staff-inquiry-dialog">
          <DialogHeader><DialogTitle>{form?.id ? "Edit Staff Inquiry" : "Add Staff Inquiry"}</DialogTitle><DialogDescription>Capture a teacher / staff job application.</DialogDescription></DialogHeader>
          {form && (
            <div className="space-y-4">
              <Section title="Personal Information" />
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div className="col-span-2 md:col-span-1"><Label className="text-xs">Full Name *</Label><Input value={form.name} onChange={(e) => setF("name", e.target.value)} data-testid="si-name" /></div>
                <div><Label className="text-xs">Email ID</Label><Input value={form.email} onChange={(e) => setF("email", e.target.value)} data-testid="si-email" /></div>
                <div><Label className="text-xs">Phone Number</Label><Input value={form.phone} onChange={(e) => setF("phone", e.target.value)} data-testid="si-phone" /></div>
                <div><Label className="text-xs">WhatsApp Number</Label><Input value={form.whatsapp} onChange={(e) => setF("whatsapp", e.target.value)} data-testid="si-whatsapp" /></div>
                <div><Label className="text-xs">Date of Birth</Label><Input type="date" value={form.dob} onChange={(e) => setF("dob", e.target.value)} data-testid="si-dob" /></div>
                <div><Label className="text-xs">Gender</Label>
                  <Select value={form.gender} onValueChange={(v) => setF("gender", v)}><SelectTrigger data-testid="si-gender"><SelectValue placeholder="Select gender" /></SelectTrigger><SelectContent>{["Male", "Female", "Other"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select>
                </div>
                <div><Label className="text-xs">Marital Status</Label>
                  <Select value={form.marital_status} onValueChange={(v) => setF("marital_status", v)}><SelectTrigger data-testid="si-marital"><SelectValue placeholder="Select status" /></SelectTrigger><SelectContent>{["Married", "Unmarried"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select>
                </div>
                <div className="col-span-2 md:col-span-3"><Label className="text-xs">Address</Label><Input value={form.address} onChange={(e) => setF("address", e.target.value)} data-testid="si-address" /></div>
              </div>

              <Section title="Job Application Details" />
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div><Label className="text-xs">Post Applied For</Label><Input value={form.post_applied_for} onChange={(e) => setF("post_applied_for", e.target.value)} placeholder="e.g. Maths Teacher" data-testid="si-post" /></div>
                <div><Label className="text-xs">Type</Label>
                  <Select value={form.staff_type} onValueChange={(v) => setF("staff_type", v)}><SelectTrigger data-testid="si-stafftype"><SelectValue /></SelectTrigger><SelectContent>{["Teaching", "Non-Teaching"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select>
                </div>
                <div><Label className="text-xs">Expected Salary (₹)</Label><Input value={form.expected_salary} onChange={(e) => setF("expected_salary", e.target.value)} data-testid="si-salary" /></div>
                <div><Label className="text-xs">Joining Availability</Label><Input type="date" value={form.joining_availability} onChange={(e) => setF("joining_availability", e.target.value)} data-testid="si-joining" /></div>
                <div><Label className="text-xs">Notice Period</Label><Input value={form.notice_period} onChange={(e) => setF("notice_period", e.target.value)} placeholder="e.g. 1 month" data-testid="si-notice" /></div>
                <div><Label className="text-xs">Application Source</Label>
                  <Select value={form.source} onValueChange={(v) => setF("source", v)}><SelectTrigger data-testid="si-source"><SelectValue placeholder="Select source" /></SelectTrigger><SelectContent>{SOURCES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select>
                </div>
                <div><Label className="text-xs">Apply Date</Label><Input type="date" value={form.apply_date} onChange={(e) => setF("apply_date", e.target.value)} data-testid="si-applydate" /></div>
              </div>

              <Section title="Education Details" />
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div><Label className="text-xs">Qualification</Label><Input value={form.qualification} onChange={(e) => setF("qualification", e.target.value)} placeholder="e.g. M.Sc, B.Ed" data-testid="si-qual" /></div>
                <div><Label className="text-xs">Subject / Specialization</Label><Input value={form.specialization} onChange={(e) => setF("specialization", e.target.value)} data-testid="si-spec" /></div>
                <div><Label className="text-xs">Board / University</Label><Input value={form.board_university} onChange={(e) => setF("board_university", e.target.value)} data-testid="si-board" /></div>
                <div><Label className="text-xs">Passing Year</Label><Input value={form.passing_year} onChange={(e) => setF("passing_year", e.target.value)} data-testid="si-year" /></div>
                <div><Label className="text-xs">% / CGPA</Label><Input value={form.cgpa} onChange={(e) => setF("cgpa", e.target.value)} data-testid="si-cgpa" /></div>
              </div>

              <Section title="Work Experience" />
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div><Label className="text-xs">Organization Name</Label><Input value={form.organization} onChange={(e) => setF("organization", e.target.value)} data-testid="si-org" /></div>
                <div><Label className="text-xs">Last Designation</Label><Input value={form.last_designation} onChange={(e) => setF("last_designation", e.target.value)} data-testid="si-lastdesig" /></div>
                <div><Label className="text-xs">Total Experience</Label><Input value={form.experience} onChange={(e) => setF("experience", e.target.value)} placeholder="e.g. 3 Years" data-testid="si-exp" /></div>
              </div>

              <Section title="Documents" />
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Resume / CV (PDF, DOC — Max 5MB)</Label><Input type="file" accept=".pdf,.doc,.docx" onChange={(e) => onFile("resume", "resume_name", e.target.files?.[0], 5)} className="text-xs" data-testid="si-resume" />{form.resume_name && <p className="text-[11px] text-emerald-600 mt-1">{form.resume_name}</p>}</div>
                <div><Label className="text-xs">Photo (JPG, PNG — Max 2MB)</Label><Input type="file" accept="image/*" onChange={(e) => onFile("photo", null, e.target.files?.[0], 2)} className="text-xs" data-testid="si-photo" />{form.photo && <img src={form.photo} alt="candidate" className="h-10 w-10 rounded object-cover mt-1" />}</div>
              </div>

              <Section title="Status & Remarks" />
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div><Label className="text-xs">Current Status</Label>
                  <Select value={form.status} onValueChange={(v) => setF("status", v)}><SelectTrigger data-testid="si-form-status"><SelectValue /></SelectTrigger><SelectContent>{ALL_STATUSES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select>
                </div>
                <div><Label className="text-xs">Interview Date & Time</Label><Input type="datetime-local" value={form.interview_at} onChange={(e) => setF("interview_at", e.target.value)} data-testid="si-form-interview" /></div>
                <div><Label className="text-xs">Rating</Label><div className="h-10 flex items-center"><Stars value={form.rating || 0} onChange={(v) => setF("rating", v)} testid="si-form-rating" /></div></div>
                <div className="col-span-2 md:col-span-3"><Label className="text-xs">Remarks</Label><Textarea rows={2} value={form.remarks} onChange={(e) => setF("remarks", e.target.value)} data-testid="si-remarks" /></div>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setForm(null)}>Cancel</Button><Button onClick={save} disabled={saving} data-testid="si-save">{saving && <Loader2 size={14} className="animate-spin mr-1.5" />}Save Inquiry</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent data-testid="si-delete-dialog">
          <DialogHeader><DialogTitle className="text-rose-600">Delete Inquiry?</DialogTitle><DialogDescription>Remove <b>{delTarget?.name}</b>'s application. This cannot be undone.</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setDelTarget(null)}>Cancel</Button><Button className="bg-rose-600 hover:bg-rose-700" onClick={doDelete} data-testid="si-delete-confirm">Delete</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <StaffForm open={staffFormOpen} onOpenChange={(o) => { setStaffFormOpen(o); if (!o) setJoinLead(null); }} edit={null} initial={staffInitial} addTitle="Join Inquiry — New Staff" onAdded={onStaffJoined} onSaved={load} />

      <Sheet open={!!resumeView} onOpenChange={(o) => !o && setResumeView(null)}>
        <SheetContent side="right" className="w-full sm:max-w-xl p-0 flex flex-col" data-testid="resume-sheet">
          <SheetHeader className="p-4 border-b"><SheetTitle className="flex items-center gap-2"><FileText size={18} className="text-indigo-600" />{resumeView?.name}'s Resume</SheetTitle></SheetHeader>
          <div className="flex-1 overflow-auto p-4">
            {resumeView && ((resumeView.resume || "").startsWith("data:application/pdf") || (resumeView.resume || "").startsWith("data:image")) ? (
              (resumeView.resume || "").startsWith("data:image")
                ? <img src={resumeView.resume} alt="resume" className="w-full rounded border" />
                : <iframe title="resume" src={resumeView.resume} className="w-full h-full min-h-[75vh] rounded border" />
            ) : (
              <div className="text-center text-slate-500 space-y-3 py-16">
                <FileText size={48} className="mx-auto opacity-40" />
                <p className="text-sm">Inline preview isn't available for this file type.</p>
                {resumeView?.resume && <a href={resumeView.resume} download={resumeView.resume_name || "resume"}><Button className="gap-1.5"><DownloadCloud size={16} />Download CV</Button></a>}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Stars({ value, onChange, readOnly, testid }) {
  return (
    <div className="flex items-center gap-0.5" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" disabled={readOnly} onClick={() => onChange && onChange(n === value ? 0 : n)} className={readOnly ? "cursor-default" : "cursor-pointer hover:scale-110 transition-transform"} title={`${n} star${n > 1 ? "s" : ""}`} data-testid={testid ? `${testid}-${n}` : undefined}>
          <Star size={15} className={n <= value ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
        </button>
      ))}
    </div>
  );
}

const fmtDT = (s) => { try { return new Date(s).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }); } catch { return s; } };

const Section = ({ title }) => <div className="text-xs font-semibold uppercase tracking-wide text-indigo-500 border-b border-slate-100 pb-1">{title}</div>;
